/**
 * Highlight Component
 * Highlights a specific element and shows explanation tooltip
 */

import React, { useEffect, useState } from 'react';
import { useTutorial } from '@/context/TutorialContext';

const Highlight: React.FC = () => {
  const { isHighlighting, highlightState, stopHighlight } = useTutorial();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [scrollOffset, setScrollOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!isHighlighting || !highlightState) return;

    const updateTargetPosition = () => {
      if (highlightState?.targetSelector) {
        const element = document.querySelector(highlightState.targetSelector);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
          setScrollOffset({
            x: window.scrollX,
            y: window.scrollY,
          });
        }
      }
    };

    updateTargetPosition();

    // Update position on scroll and resize
    window.addEventListener('scroll', updateTargetPosition);
    window.addEventListener('resize', updateTargetPosition);

    return () => {
      window.removeEventListener('scroll', updateTargetPosition);
      window.removeEventListener('resize', updateTargetPosition);
    };
  }, [isHighlighting, highlightState]);

  if (!isHighlighting || !highlightState || !targetRect) {
    return null;
  }

  const padding = 8;
  const highlightStyle = {
    top: `${targetRect.top + scrollOffset.y - padding}px`,
    left: `${targetRect.left + scrollOffset.x - padding}px`,
    width: `${targetRect.width + padding * 2}px`,
    height: `${targetRect.height + padding * 2}px`,
  };

  // Calculate tooltip position
  const tooltipPositions = {
    top: targetRect.top + scrollOffset.y - 120,
    bottom: targetRect.bottom + scrollOffset.y + 20,
    left: targetRect.left + scrollOffset.x - 300,
    right: targetRect.right + scrollOffset.x + 20,
  };

  let tooltipStyle: React.CSSProperties = { position: 'absolute', zIndex: 9999 };
  const position = highlightState.position || 'bottom';

  switch (position) {
    case 'top':
      tooltipStyle.top = tooltipPositions.top;
      tooltipStyle.left = targetRect.left + scrollOffset.x + (targetRect.width - 280) / 2;
      break;
    case 'bottom':
      tooltipStyle.top = tooltipPositions.bottom;
      tooltipStyle.left = targetRect.left + scrollOffset.x + (targetRect.width - 280) / 2;
      break;
    case 'left':
      tooltipStyle.top = targetRect.top + scrollOffset.y + (targetRect.height - 80) / 2;
      tooltipStyle.left = tooltipPositions.left;
      break;
    case 'right':
      tooltipStyle.top = targetRect.top + scrollOffset.y + (targetRect.height - 80) / 2;
      tooltipStyle.left = tooltipPositions.right;
      break;
  }

  return (
    <>
      {/* Overlay backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={stopHighlight} />

      {/* Highlight box */}
      <div
        className="fixed border-4 border-blue-500 rounded-lg bg-blue-100/10 pointer-events-none z-40 animate-pulse"
        style={highlightStyle}
      />

      {/* Tooltip with explanation */}
      <div
        className="fixed bg-white dark:bg-gray-900 shadow-2xl rounded-lg border-2 border-blue-500 w-80 p-6 z-50"
        style={tooltipStyle}
      >
        {/* Arrow */}
        {highlightState.showArrow && (
          <div
            className="absolute w-4 h-4 bg-white dark:bg-gray-900 border-2 border-blue-500 rotate-45"
            style={
              position === 'top'
                ? { bottom: '-10px', left: '50%', transform: 'translateX(-50%)' }
                : position === 'left'
                  ? { right: '-10px', top: '50%', transform: 'translateY(-50%)' }
                  : { top: '-10px', left: '50%', transform: 'translateX(-50%)' }
            }
          />
        )}

        <div className="text-left">
          {highlightState.title && (
            <h3 className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-2">{highlightState.title}</h3>
          )}

          {highlightState.description && (
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{highlightState.description}</p>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={stopHighlight}
              className="px-4 py-2 text-sm font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
            {highlightState.onNext && (
              <button
                onClick={() => {
                  highlightState.onNext?.();
                  stopHighlight();
                }}
                className="px-4 py-2 text-sm font-medium bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Highlight;
